import React, { useState, useEffect } from 'react';
import { 
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Table,
  Space,
  message,
  Row,
  Col,
  Divider,
  Typography,
  Popconfirm,
  Switch,
  Radio,
  AutoComplete,
  FormInstance
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { FeeDTO, FeeType, FeeCode, FeeCollectionTiming } from '@/shared/api/model';
import { useTheme } from '@/components/common/ThemeProvider';
import { 
  useCreateFee, 
  useUpdateFee, 
  useDeleteFee 
} from '@/shared/api/client/fee-resource';

const { Option } = Select;
const { Text } = Typography;

export interface GlAccount {
  id: string;
  name: string;
}

interface FeesPanelProps {
  fees: FeeDTO[];
  glNo: string;
  onFeesChange: (fees: FeeDTO[]) => void;
  glAccounts: GlAccount[];
  acAccounts: GlAccount[];
  isLoading?: boolean;
  standalone?: boolean;
  externalForm?: FormInstance;
}

const AccountSelector = ({ 
  accounts, 
  value, 
  onChange, 
  placeholder,
  dark = false
}: { 
  accounts: GlAccount[], 
  value?: string, 
  onChange: (value: string) => void, 
  placeholder: string,
  dark?: boolean
}) => {
  const options = accounts.map(account => ({
    value: account.id,
    label: `${account.name} (${account.id})`
  }));

  return (
    <AutoComplete
      style={{ width: '100%' }}
      options={options}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      filterOption={(inputValue, option) =>
        option?.label?.toString().toLowerCase().includes(inputValue.toLowerCase()) || false
      }
      className={dark ? 'dark-mode-select' : ''}
    />
  );
};

const FeesPanel: React.FC<FeesPanelProps> = ({
  fees = [],
  glNo,
  onFeesChange,
  glAccounts = [],
  acAccounts = [],
  isLoading = false,
  standalone = false,
  externalForm
}) => {
  const [form] = Form.useForm();
  const [editingFee, setEditingFee] = useState<FeeDTO | null>(null);
  const [feesList, setFeesList] = useState<FeeDTO[]>(fees);
  const { dark } = useTheme();
  
  const formToUse = externalForm || form;
  
  // Add mutations for fee operations
  const { mutateAsync: createFeeMutation } = useCreateFee();
  const { mutateAsync: updateFeeMutation } = useUpdateFee();
  const { mutateAsync: deleteFeeMutation } = useDeleteFee();

  useEffect(() => {
    setFeesList(fees);
  }, [fees]);

  const handleAddFee = () => {
    // @ts-ignore
    formToUse.validateFields().then(values => {
      const newFee: FeeDTO = {
        ...values,
        id: editingFee?.id,
        productPropertiesGlNo:glNo,
        active: true
      };

      // If there's a productGlNo from a parent component, add it to the fee
      if (fees.length > 0 && fees[0].productPropertiesGlNo) {
        newFee.productPropertiesGlNo = fees[0].productPropertiesGlNo;
      }

      if (editingFee) {
        // Update existing fee
        if (editingFee.id) {
          updateFeeMutation({ id: editingFee.id, data: newFee })
            .then(response => {
              const updatedFee = response;
              const updatedFees = feesList.map(fee => 
                fee.id === editingFee.id ? updatedFee : fee
              );
              setFeesList(updatedFees);
              onFeesChange(updatedFees);
              setEditingFee(null);
              formToUse.resetFields();
              message.success('Fee updated successfully');
            })
            .catch(error => {
              console.error('Error updating fee:', error);
              message.error('Failed to update fee');
            });
        } else {
          // Handle case where editing a fee that hasn't been saved yet
          const updatedFees = feesList.map(fee => 
            fee === editingFee ? newFee : fee
          );
          setFeesList(updatedFees);
          onFeesChange(updatedFees);
          setEditingFee(null);
          formToUse.resetFields();
          message.success('Fee updated successfully');
        }
      } else {
        // Create new fee
        createFeeMutation({ data: newFee })
          .then(response => {
            const createdFee = response;
            const updatedFees = [...feesList, createdFee];
            setFeesList(updatedFees);
            onFeesChange(updatedFees);
            formToUse.resetFields();
            message.success('Fee added successfully');
          })
          .catch(error => {
            console.error('Error creating fee:', error);
            message.error('Failed to add fee');
          });
      }
    });
  };

  const handleEditFee = (fee: FeeDTO) => {
    setEditingFee(fee);
    formToUse.setFieldsValue({
      feeCode: fee.feeCode,
      feeName: fee.feeName,
      description: fee.description,
      feeType: fee.feeType,
      feeValue: fee.feeValue,
      glNo: fee.glNo,
      acNo: fee.acNo,
      collectionTiming: fee.collectionTiming || FeeCollectionTiming.DISBURSEMENT
    });
  };

  const handleDeleteFee = (feeToDelete: FeeDTO) => {
    if (feeToDelete.id) {
      // Delete from backend if it has an ID
      deleteFeeMutation({ id: feeToDelete.id })
        .then(() => {
          const updatedFees = feesList.filter(fee => fee !== feeToDelete);
          setFeesList(updatedFees);
          onFeesChange(updatedFees);
          message.success('Fee deleted successfully');
        })
        .catch(error => {
          console.error('Error deleting fee:', error);
          message.error('Failed to delete fee');
        });
    } else {
      // Just remove from local state if it doesn't have an ID yet
      const updatedFees = feesList.filter(fee => fee !== feeToDelete);
      setFeesList(updatedFees);
      onFeesChange(updatedFees);
      message.success('Fee deleted successfully');
    }
  };

  const handleCancelEdit = () => {
    setEditingFee(null);
    formToUse.resetFields();
  };

  const getFeeCodeDescription = (code: string) => {
    switch (code) {
      case FeeCode.COMMISSION_FEE:
        return 'Commission';
      case FeeCode.INSURANCE_FEE:
        return 'Insurance';
      case FeeCode.APPLICATION_FEE:
        return 'Application';
      case FeeCode.PROCESSING_FEE:
        return 'Processing';
      case FeeCode.CUSTOM_1:
        return 'Custom Fee 1';
      case FeeCode.CUSTOM_2:
        return 'Custom Fee 2';
      case FeeCode.CUSTOM_3:
        return 'Custom Fee 3';
      case FeeCode.CUSTOM_4:
        return 'Custom Fee 4';
      default:
        return code;
    }
  };
  
  const getCollectionTimingDescription = (timing: string) => {
    switch (timing) {
      case FeeCollectionTiming.DISBURSEMENT:
        return 'On Disbursement';
      case FeeCollectionTiming.REPAYMENT:
        return 'During Repayment';
      default:
        return timing;
    }
  };

  const columns = [
    {
      title: 'Fee Type',
      dataIndex: 'feeCode',
      key: 'feeCode',
      render: (text: string) => getFeeCodeDescription(text)
    },
    {
      title: 'Fee Name',
      dataIndex: 'feeName',
      key: 'feeName'
    },
    {
      title: 'Type',
      dataIndex: 'feeType',
      key: 'feeType',
      render: (text: string) => text === FeeType.PERCENTAGE ? 'Percentage (%)' : 'Fixed Amount'
    },
    {
      title: 'Value',
      dataIndex: 'feeValue',
      key: 'feeValue',
      render: (value: number, record: FeeDTO) => {
        if (record.feeType === FeeType.PERCENTAGE) {
          return `${value}%`;
        }
        return value?.toLocaleString();
      }
    },
    {
      title: 'Collection Timing',
      dataIndex: 'collectionTiming',
      key: 'collectionTiming',
      render: (text: string) => getCollectionTimingDescription(text)
    },
    {
      title: 'GL Account',
      dataIndex: 'glNo',
      key: 'glNo',
      render: (text: string) => {
        const account = glAccounts.find(acc => acc.id === text);
        return account ? `${account.id} - ${account.name}` : text;
      }
    },
    {
      title: 'AC Account',
      dataIndex: 'acNo',
      key: 'acNo',
      render: (text: string) => {
        const account = acAccounts.find(acc => acc.id === text);
        return account ? `${account.id} - ${account.name}` : text;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: FeeDTO) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEditFee(record)}
            type="link"
            size="small"
          />
          <Popconfirm
            title="Are you sure you want to delete this fee?"
            onConfirm={() => handleDeleteFee(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              icon={<DeleteOutlined />} 
              type="link"
              danger
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const formContent = (
    <>
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="feeCode"
            label="Fee Type"
            rules={[{ required: true, message: 'Please select a fee type' }]}
          >
            <Select placeholder="Select fee type">
              {Object.values(FeeCode).map((code) => (
                <Option key={code} value={code}>
                  {getFeeCodeDescription(code)}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="feeName"
            label="Fee Name"
            rules={[{ required: true, message: 'Please enter fee name' }]}
          >
            <Input placeholder="Fee Name" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input placeholder="Description" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item
            name="feeType"
            label="Fee Calculation Type"
            rules={[{ required: true, message: 'Please select fee calculation type' }]}
          >
            <Radio.Group>
              <Radio value={FeeType.FIXED_AMOUNT}>Fixed Amount</Radio>
              <Radio value={FeeType.PERCENTAGE}>Percentage</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item
            name="feeValue"
            label="Fee Value"
            rules={[{ required: true, message: 'Please enter fee value' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              placeholder="Value"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <Form.Item
            name="collectionTiming"
            label="Fee Collection Timing"
            rules={[{ required: true, message: 'Please select when the fee is collected' }]}
          >
            <Radio.Group>
              <Radio value={FeeCollectionTiming.DISBURSEMENT}>On Disbursement</Radio>
              <Radio value={FeeCollectionTiming.REPAYMENT}>During Repayment</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">Fee Accounting</Divider>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="glNo"
            label="GL Account"
            rules={[{ required: true, message: 'Please select GL account' }]}
          >
            <AccountSelector
              accounts={glAccounts}
              placeholder="Select GL Account"
              onChange={(value) => formToUse.setFieldsValue({ glNo: value })}
              dark={dark}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="acNo"
            label="AC Account"
            rules={[{ required: true, message: 'Please select AC account' }]}
          >
            <AccountSelector
              accounts={acAccounts}
              placeholder="Select AC Account"
              onChange={(value) => formToUse.setFieldsValue({ acNo: value })}
              dark={dark}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row justify="end" gutter={16}>
        {editingFee && (
          <Col>
            <Button onClick={handleCancelEdit}>Cancel</Button>
          </Col>
        )}
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddFee}
          >
            {editingFee ? 'Update Fee' : 'Add Fee'}
          </Button>
        </Col>
      </Row>
    </>
  );

  return (
    <div className={dark ? 'dark-mode' : ''}>
      <Card title="Fee Configuration" style={{ marginBottom: 16 }}>
        {standalone ? (
          <Form
            form={formToUse}
            layout="vertical"
            initialValues={{
              feeType: FeeType.FIXED_AMOUNT,
              collectionTiming: FeeCollectionTiming.DISBURSEMENT
            }}
          >
            {formContent}
          </Form>
        ) : (
          <div>
            {formContent}
          </div>
        )}
      </Card>

      <Card title="Configured Fees">
        <Table
          dataSource={feesList}
          columns={columns}
          rowKey={(record) => record.id?.toString() || Math.random().toString()}
          pagination={false}
          loading={isLoading}
          locale={{ emptyText: 'No fees configured yet' }}
        />
      </Card>
    </div>
  );
};

export default FeesPanel;
